import _ from 'lodash';
import { isDate } from 'helpers/helpers';
import { UNKNOWN_DATUM } from 'business/constants';
import { DataTypes as DT } from 'business/definitions';
import { getBins } from '../eChartHelpers';
import { hasLessThan12HoursDifference } from '../dateHelper';
import {
    fillGapInYearlyPivotedData,
    getYearlyComparisonSettingsForScale,
} from '../yearlyComparison/settings';
import { isYearlyComparisonScale } from '../yearlyComparison/scale';

const isYearlyComparisonEnabled = (
    isYearlyComparison: boolean,
    xColumn: ResponseColumnT | undefined,
): xColumn is ResponseColumnT =>
    isYearlyComparison && xColumn?.type === 'category' && !!xColumn.key;

const isDateType = (
    xType: DataTypesT,
    xValues: ResponseDataValueT[],
): xValues is number[] => isDate(xType) || xType === DT.time;
// When we cumulate data over a time series
// there might be some month (for example) that could have no values.
// We still want to display the month's cumulated amount
// with the value of the previous month.
// The next lines of code add to the series the month (for example) that
// are not defined in the serie.
function fillGapsInCumulatedData(
    columns: ResponseColumnT[],
    sumColumnKey: string,
    groupedData: Record<string, ResponseDataT[]>,
    isYearlyComparison: boolean,
    maxDate: Date,
) {
    const xColumn = columns.find((col) => col.key === sumColumnKey);
    const filledData = { ...groupedData };

    const additionalColumns = columns.filter(
        (column) =>
            !column.isMetric &&
            !['year', 'month', 'day', 'quarter'].includes(column.key),
    );
    const scaleValues = new Set(
        isYearlyComparisonEnabled(isYearlyComparison, xColumn)
            ? Object.values(filledData).flatMap((dg) =>
                  dg.map((d) => d[xColumn.key]),
              )
            : [],
    );

    Object.values(filledData).forEach((dataGroup: ResponseDataT[]) => {
        if (!isYearlyComparison && xColumn) {
            const xType = xColumn.type;
            const xScale = xColumn.scale;
            const xValues = _.uniq(
                _.flatten(Object.values(filledData)).map(
                    (d) => d[sumColumnKey],
                ),
            ).sort();
            if (isDateType(xType, xValues)) {
                const bins = getBins(xType, xScale, xValues);
                if (xValues.length < bins.length) {
                    bins.forEach((bin) => {
                        if (
                            !xValues.includes(bin) &&
                            xValues.every(
                                (xVal) =>
                                    !hasLessThan12HoursDifference(xVal, bin),
                            )
                        ) {
                            dataGroup.push({ [sumColumnKey]: bin });
                        }
                    });
                }
            }
        }

        if (
            isYearlyComparisonEnabled(isYearlyComparison, xColumn) &&
            isYearlyComparisonScale(xColumn.key)
        ) {
            fillGapInYearlyPivotedData(
                dataGroup,
                xColumn.key,
                additionalColumns,
                Array.from(scaleValues),
                maxDate,
            );
        }
    });
    return filledData;
}

/**
 * Cumulate metrics (specified in cumulatedMetricsKeys)
 * along one dimension (given in sumColumnKey).
 */
export function cumulateMetrics(
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    cumulatedMetricsKeys: string[],
    sumColumnKey: string,
    maxDate: Date,
): FlowAnyObject {
    // TODO: improve typing
    if (cumulatedMetricsKeys.length === 0) {
        return data;
    }
    // The dimension on which to sum is the x values of a chart, determined by getOrderedColumns
    const groupColumns = columns.filter(
        (col) => col.key !== sumColumnKey && !col.isMetric,
    );
    // We group data based on the columns values that are not the pivot
    const groupedData = _.groupBy(data, (d) => {
        let hash = '';
        groupColumns.forEach((col) => {
            // we need to handle tree structured data points that are a dict
            // FIXME: we need to do this check at many places in the codebase, find a better solution
            const value =
                // @ts-expect-error [TS migration] Was not detected by flow
                d[col.key].value != null ? d[col.key].value : d[col.key];
            hash += value.toString();
        });
        return hash;
    });

    const isYearlyComparison = isYearlyComparisonScale(sumColumnKey);

    const filledData = fillGapsInCumulatedData(
        columns,
        sumColumnKey,
        groupedData,
        isYearlyComparison,
        maxDate,
    );
    // We sort and sum inside each group of data

    Object.values(filledData).forEach((dataGroup: ResponseDataT[]) => {
        if (isYearlyComparison) {
            const yearlyComparisonSettings =
                getYearlyComparisonSettingsForScale(sumColumnKey);
            const sortDateNames = yearlyComparisonSettings.sort;
            dataGroup.sort((a, b) =>
                sortDateNames(
                    // TODO [typing getData hooks] try to remove these non-null assertion
                    a[sumColumnKey]!.toString(),
                    b[sumColumnKey]!.toString(),
                ),
            );
        } else {
            // @ts-expect-error [TS migration] (previously $FlowFixMe)
            dataGroup.sort((a, b) => a[sumColumnKey] - b[sumColumnKey]);
        }

        cumulatedMetricsKeys.forEach((key) => {
            // @ts-expect-error [TS migration] Was not detected by flow
            const sumCum = [];
            dataGroup.reduce((a, b, i) => {
                // When the value does not exists or is unknown, we replace it by 0.
                // This does not stop the sum, but repeats the same value.
                const value =
                    // @ts-expect-error [TS migration] (previously $FlowFixMe) : Data response has several types
                    b[key] === UNKNOWN_DATUM || b[key] == null ? a : a + b[key];
                sumCum[i] = value;
                // @ts-expect-error [TS migration] Was not detected by flow
                return sumCum[i];
            }, 0);
            dataGroup.forEach((d, i) => {
                // @ts-expect-error [TS migration] Was not detected by flow
                d[key] = sumCum[i];
            });
        });
    });
    return _.flatten(Object.values(filledData));
}
